/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import * as React from "react";
import { Stack, SpinButton, Position, Checkbox } from "@fluentui/react";

import styles from "./FieldDefinitionPanel.module.css";

import { HuxTextBox } from "@nuance/hux-components";
import { IFieldDefinitionPanelTextProps, IFieldDefinitionPanelTextState } from ".";
import { FieldDefinitionPanelTextCommands } from "./FieldDefinitionPanelTextViewModel";
import { panelFooterProps } from "./FieldDefinitionPanelStackTokens";
import { IPanelFooterProps } from "./IPanelFooterProps";
import FieldDefinitionPanel from "./FieldDefinitionPanel";

const fieldDefinitionPanelTextCommands = new FieldDefinitionPanelTextCommands();
const cmds = fieldDefinitionPanelTextCommands.Cmds;

const FieldDefinitionPanelText = (props: IFieldDefinitionPanelTextProps): JSX.Element => {
    const initialState: IFieldDefinitionPanelTextState = {
        common: { ...props.common },
        data: { ...props.data },
        errors: {},
        touched: {},
        isValid: !props.common.isNew,
        isDirty: false
    };

    const [state, dispatch] = React.useReducer(
        fieldDefinitionPanelTextCommands.Reducer,
        initialState
    );

    const onSaveFieldDefinition = () => {
        if (props.common.onSaveContent != undefined) {
            const saveData = fieldDefinitionPanelTextCommands.Save(state);
            props.common.onSaveContent(saveData);
        }
    };

    const onSpinMaxDecrement = () => {
        dispatch(cmds.onSpinMaxDecrement());
    };

    const onSpinMaxIncrement = () => {
        dispatch(cmds.onSpinMaxIncrement());
    };

    const onMaxChange = (_event: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        dispatch(cmds.onMaxLengthChanged(newValue));
    };

    const onMinChange = (_event: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        dispatch(cmds.onMinLengthChanged(newValue));
    };

    const onSpinMinDecrement = () => {
        dispatch(cmds.onSpinMinDecrement());
    };

    const onSpinMinIncrement = () => {
        dispatch(cmds.onSpinMinIncrement());
    };

    const onIncludeRegexChange = () => {
        dispatch(cmds.onIncludeRegexChanged());
    };

    const footerProps: IPanelFooterProps = {
        onSave: onSaveFieldDefinition,
        onCancel: props.common.onClosePanel,
        isNew: state.common.isNew,
        isDirty: state.isDirty,
        isValid: state.isValid,
        panelFooterProps: panelFooterProps
    };

    return (
        <FieldDefinitionPanel
            title={props.common.panelTitle}
            isOpen={props.common.isOpen}
            onClosePanel={props.common.onClosePanel}
            footerProps={footerProps}
            settingName={state.data.settingName}
            onChangeSettingName={(name, value) => dispatch(cmds.onTextChanged({ name, value }))}
            settingNameErrorMessage={state.touched.settingName ? state.errors.settingName : ""}
            onBlurSettingName={name => dispatch(cmds.onBlur(name))}
            settingNameReadOnly={!props.common.isNew}
            titleValue={state.data.title}
            onChangeTitle={(name, value) => dispatch(cmds.onTextChanged({ name, value }))}
            titleErrorMessage={state.touched.title ? state.errors.title : ""}
            onBlurTitle={name => dispatch(cmds.onBlur(name))}
            showRequired={true}
            isRequired={state.data.isRequired}
            onChangeRequired={() => dispatch(cmds.onIsRequiredChanged())}
            tooltipText={state.data.tooltip}
            helperText={state.data.helper}
            onHelpTextChange={changeData => {
                dispatch(cmds.onHelpTextChange(changeData));
            }}
            accessLevel={state.data.accessLevels}
            onAccessLevelChange={accessLevel => dispatch(cmds.onAccessLevelChanged(accessLevel))}
            auditChanges={state.data.auditChanges}
            onAuditChangesChanged={(checked: boolean) => dispatch(cmds.onAuditChanged(checked))}
            controlType={state.data.definitionType}
        >
            <Stack horizontal tokens={{ childrenGap: 11 }}>
                <SpinButton
                    label="Min Length"
                    labelPosition={Position.top}
                    className={styles.minLength}
                    value={state.data.minLength.toString()}
                    min={0}
                    step={1}
                    incrementButtonAriaLabel="Increase value by 1"
                    decrementButtonAriaLabel="Decrease value by 1"
                    onIncrement={onSpinMinIncrement}
                    onDecrement={onSpinMinDecrement}
                    onChange={onMinChange}
                />
                <SpinButton
                    label="Max Length"
                    labelPosition={Position.top}
                    className={styles.maxLength}
                    value={state.data.maxLength.toString()}
                    min={0}
                    incrementButtonAriaLabel="Increase value by 1"
                    decrementButtonAriaLabel="Decrease value by 1"
                    onIncrement={onSpinMaxIncrement}
                    onDecrement={onSpinMaxDecrement}
                    onChange={onMaxChange}
                />
            </Stack>
            <HuxTextBox
                label="Default value"
                name="defaultValue"
                title="Default value setting value"
                value={state.data.defaultValue}
                errorMessage={
                    state.touched.defaultValue || state.touched.regexValue
                        ? state.errors.defaultValue
                        : ""
                }
                onChange={(e, value) =>
                    dispatch(
                        cmds.onTextChanged({
                            name: e.currentTarget.name,
                            value
                        })
                    )
                }
                onBlur={e => dispatch(cmds.onBlur(e.currentTarget.name))}
            />
            <Checkbox
                label="Include a regular expression"
                checked={state.data.includeRegex}
                onChange={onIncludeRegexChange}
            ></Checkbox>
            {state.data.includeRegex && (
                <>
                    <HuxTextBox
                        label="Regular expression"
                        name="regexValue"
                        title="Add a regular expression (regex) for validation."
                        infoText="Add a regular expression (regex) for validation."
                        required
                        value={state.data.regexValue}
                        errorMessage={state.touched.regexValue ? state.errors.regexValue : ""}
                        onChange={(e, value) =>
                            dispatch(
                                cmds.onTextChanged({
                                    name: e.currentTarget.name,
                                    value
                                })
                            )
                        }
                        onBlur={e => dispatch(cmds.onBlur(e.currentTarget.name))}
                    />
                    <HuxTextBox
                        label="Error for regular expression"
                        name="regexErrorMessage"
                        title="Include an error message if the validation fails to help the user understand the correct format."
                        infoText="Include an error message if the validation fails to help the user understand the correct format."
                        required
                        value={state.data.regexErrorMessage}
                        errorMessage={
                            state.touched.regexErrorMessage ? state.errors.regexErrorMessage : ""
                        }
                        onChange={(e, value) =>
                            dispatch(
                                cmds.onTextChanged({
                                    name: e.currentTarget.name,
                                    value
                                })
                            )
                        }
                        onBlur={e => dispatch(cmds.onBlur(e.currentTarget.name))}
                    />
                </>
            )}
        </FieldDefinitionPanel>
    );
};

const TextPanel = FieldDefinitionPanelText;
export { TextPanel };
